import React, { useState, useEffect, useRef } from "react";
import { useReactToPrint } from "react-to-print";
import { IoClose, IoPrint, IoDownload } from "react-icons/io5";
import { API_BASE_URL } from "utils/api";
import { getToken } from "utils/auth";
import axios from "axios";

const ReceiptBuilderModal = ({ isOpen, onClose, client }) => {
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen) {
      fetchUserProfile();
    }
  }, [isOpen]);

  const fetchUserProfile = async () => {
    try {
      setLoading(true);
      const token = getToken();
      const response = await axios.get(`${API_BASE_URL}/users/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserProfile(response.data?.data || response.data);
    } catch (error) {
      console.error("Failed to fetch user profile", error);
    } finally {
      setLoading(false);
    }
  };

  const receiptRef = useRef();

  const handlePrint = useReactToPrint({
    contentRef: receiptRef,
    documentTitle: `Receipt-${client?.firstName || client?.name || "Client"}`,
  });

  if (!isOpen) return null;

  const formatCurrency = (value) => {
    if (!value && value !== 0) return "-";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const totalReceived = client?.installments?.reduce((sum, inst) => sum + (inst.amount || 0), 0) || 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm print:bg-white print:backdrop-blur-none">
      <div className="flex flex-col w-full max-w-4xl h-[90vh] sm:h-auto max-h-[90vh] rounded-2xl bg-white shadow-xl dark:bg-navy-800 print:shadow-none print:w-full print:h-auto print:max-h-none print:max-w-none print:rounded-none">
        
        {/* Header Controls - Hidden in print */}
        <div className="flex items-center justify-between p-6 border-b dark:border-white/10 print:hidden">
          <h3 className="text-xl font-bold text-navy-700 dark:text-white">
            Receipt Builder
          </h3>
          <div className="flex items-center gap-3">
            <button
              onClick={handlePrint}
              className="flex items-center gap-2 rounded-xl bg-brand-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-brand-600 dark:bg-brand-400 dark:hover:bg-brand-300"
            >
              <IoPrint className="text-lg" />
              Print Receipt
            </button>
            <button
              onClick={onClose}
              className="rounded-full p-2 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-navy-700"
            >
              <IoClose className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Receipt Content */}
        <div className="flex-1 overflow-y-auto p-8 print:p-0 bg-gray-50/50 dark:bg-navy-900 print:bg-white" id="receipt-content">
          <div ref={receiptRef} className="mx-auto w-full max-w-3xl bg-white p-8 rounded-xl shadow-sm border border-gray-100 dark:border-navy-700 dark:bg-navy-800 print:shadow-none print:border-none print:p-4">
            
            {loading ? (
              <div className="flex justify-center py-20">
                <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-brand-500"></div>
              </div>
            ) : (
              <>
                {/* Receipt Header Section */}
                <div className="flex justify-between items-start mb-10 pb-6 border-b border-gray-100 dark:border-navy-600">
                  <div className="flex items-start gap-5">
                    {userProfile?.companyLogo ? (
                      <img
                        src={userProfile.companyLogo.startsWith('http') ? userProfile.companyLogo : `${API_BASE_URL}${userProfile.companyLogo}`}
                        alt="Company Logo"
                        className="h-20 w-20 object-contain rounded-xl border border-gray-100 dark:border-navy-600 p-1 bg-white"
                      />
                    ) : (
                      <div className="flex h-20 w-20 items-center justify-center rounded-xl bg-brand-50 text-brand-500 dark:bg-navy-700 dark:text-white text-3xl font-bold">
                        {userProfile?.companyName?.charAt(0) || "C"}
                      </div>
                    )}
                    <div className="pt-1">
                      <h1 className="text-2xl font-black text-navy-900 dark:text-white uppercase tracking-wider leading-none">
                        {userProfile?.companyName || "Your Company"}
                      </h1>
                      {userProfile?.companyShortName && (
                        <p className="text-sm font-bold text-brand-500 dark:text-brand-400 mt-2">
                          {userProfile.companyShortName}
                        </p>
                      )}
                      <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mt-1">
                        {userProfile?.email}
                      </p>
                      {userProfile?.gstNumber && (
                        <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mt-1">
                          GSTIN: <span className="font-bold text-navy-900 dark:text-white">{userProfile.gstNumber}</span>
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="text-right pt-1">
                    <h2 className="text-4xl font-light text-navy-800 dark:text-gray-200 tracking-[0.2em] uppercase mb-4">
                      Receipt
                    </h2>
                    <p className="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-1">
                      Date: <span className="text-navy-900 dark:text-white font-bold ml-1">{formatDate(new Date())}</span>
                    </p>
                    <p className="text-sm font-semibold text-gray-600 dark:text-gray-400">
                      Receipt #: <span className="text-navy-900 dark:text-white font-bold ml-1">REC-{Math.floor(1000 + Math.random() * 9000)}</span>
                    </p>
                  </div>
                </div>

                {/* Client Info Section */}
                <div className="mb-10 flex justify-between">
                  <div className="w-1/2 pr-4">
                    <h3 className="text-[11px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-[0.15em] mb-3">Billed To</h3>
                    <p className="text-lg font-bold text-navy-900 dark:text-white mb-1 leading-tight">
                      {client?.firstName} {client?.lastName}
                    </p>
                    {client?.companyName && (
                      <p className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-1">{client.companyName}</p>
                    )}
                    {client?.email && (
                      <p className="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-1">{client.email}</p>
                    )}
                    {client?.phone && (
                      <p className="text-sm font-semibold text-gray-600 dark:text-gray-400">{client.phone}</p>
                    )}
                  </div>
                  <div className="w-1/2 pl-4 flex flex-col items-end text-right">
                    <div className="w-fit min-w-[200px]">
                      <h3 className="text-[11px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-[0.15em] mb-3 text-left">Address & Status</h3>
                      <div className="flex flex-col gap-1 text-left">
                        {client?.houseNameOrNumber && (
                          <div className="flex justify-between gap-4">
                            <span className="text-sm font-semibold text-gray-500 dark:text-gray-400">House/No:</span>
                            <span className="text-sm font-bold text-navy-900 dark:text-white text-right">{client.houseNameOrNumber}</span>
                          </div>
                        )}
                        {client?.localityOrLandmark && (
                          <div className="flex justify-between gap-4">
                            <span className="text-sm font-semibold text-gray-500 dark:text-gray-400">Locality:</span>
                            <span className="text-sm font-bold text-navy-900 dark:text-white text-right">{client.localityOrLandmark}</span>
                          </div>
                        )}
                        {client?.pinCode && (
                          <div className="flex justify-between gap-4">
                            <span className="text-sm font-semibold text-gray-500 dark:text-gray-400">PIN:</span>
                            <span className="text-sm font-bold text-navy-900 dark:text-white text-right">{client.pinCode}</span>
                          </div>
                        )}
                        {client?.projectStatus && (
                          <div className="flex justify-between gap-4">
                            <span className="text-sm font-semibold text-gray-500 dark:text-gray-400">Status:</span>
                            <span className="text-sm font-bold text-navy-900 dark:text-white text-right">{client.projectStatus}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Installments Table */}
                <div className="mb-10 rounded-xl overflow-hidden border border-gray-100 dark:border-navy-600 bg-white dark:bg-navy-800">
                  <table className="w-full text-left border-collapse">
                    <thead className="bg-gray-50/80 dark:bg-navy-900/50">
                      <tr>
                        <th className="py-4 px-6 text-xs font-bold text-navy-800 dark:text-white uppercase tracking-wider border-b border-gray-100 dark:border-navy-600 w-1/6">Date</th>
                        <th className="py-4 px-6 text-xs font-bold text-navy-800 dark:text-white uppercase tracking-wider border-b border-gray-100 dark:border-navy-600 w-2/5">Particular</th>
                        <th className="py-4 px-6 text-xs font-bold text-navy-800 dark:text-white uppercase tracking-wider border-b border-gray-100 dark:border-navy-600 text-right">Qty</th>
                        <th className="py-4 px-6 text-xs font-bold text-navy-800 dark:text-white uppercase tracking-wider border-b border-gray-100 dark:border-navy-600 text-right">Rate</th>
                        <th className="py-4 px-6 text-xs font-bold text-navy-800 dark:text-white uppercase tracking-wider border-b border-gray-100 dark:border-navy-600 text-right w-1/5">Amount</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50 dark:divide-navy-700">
                      {client?.installments && client.installments.length > 0 ? (
                        client.installments.map((inst, idx) => (
                          <tr key={idx} className="hover:bg-gray-50/50 dark:hover:bg-navy-900/30 transition-colors">
                            <td className="py-4 px-6 text-sm font-semibold text-gray-700 dark:text-gray-300">
                              {formatDate(inst.date)}
                            </td>
                            <td className="py-4 px-6 text-sm font-semibold text-gray-700 dark:text-gray-300">
                              {inst.explanation}
                            </td>
                            <td className="py-4 px-6 text-sm font-semibold text-gray-700 dark:text-gray-300 text-right">
                              {inst.quantity ? `${inst.quantity} ${inst.unit || ""}`.trim() : "-"}
                            </td>
                            <td className="py-4 px-6 text-sm font-semibold text-gray-700 dark:text-gray-300 text-right">
                              {inst.rate ? formatCurrency(inst.rate) : "-"}
                            </td>
                            <td className="py-4 px-6 text-sm font-bold text-navy-900 dark:text-white text-right">
                              {formatCurrency(inst.amount)}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="5" className="py-10 text-center text-sm font-medium text-gray-400 italic">
                            No payment history available
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Totals Section */}
                <div className="flex justify-between items-end mb-6">
                  <div className="w-1/2">
                    {userProfile?.companySeal && (
                      <div className="pl-4">
                        <img 
                          src={userProfile.companySeal.startsWith('http') ? userProfile.companySeal : `${API_BASE_URL}${userProfile.companySeal}`}
                          alt="Company Seal"
                          className="max-h-32 max-w-[180px] object-contain opacity-90 mix-blend-multiply dark:mix-blend-normal"
                        />
                      </div>
                    )}
                  </div>
                  <div className="w-1/2 min-w-[300px] flex flex-col gap-2">
                    <div className="flex justify-between items-center py-3 px-4 rounded-lg bg-gray-50 dark:bg-navy-800">
                      <span className="text-sm font-bold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Total Received</span>
                      <span className="text-xl font-black text-navy-900 dark:text-white">{formatCurrency(totalReceived)}</span>
                    </div>
                    {client?.estimatedValue > 0 && (
                      <div className="flex justify-between items-center py-3 px-4 rounded-lg bg-brand-50 dark:bg-navy-900/50 mt-1">
                        <span className="text-sm font-bold text-brand-700 dark:text-brand-300 uppercase tracking-wider">Balance Due</span>
                        <span className="text-xl font-black text-brand-600 dark:text-brand-400">
                          {formatCurrency(Math.max(0, client.estimatedValue - totalReceived))}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Footer Section */}
                <div className="mt-16 pt-8 border-t border-gray-100 dark:border-navy-600 text-center">
                  <p className="text-xs font-medium text-gray-400 tracking-widest uppercase mb-1">
                    Thank you for your business
                  </p>
                  <p className="text-xs text-gray-400">
                    If you have any questions about this receipt, please contact {userProfile?.email || "us"}.
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReceiptBuilderModal;
